import { FilePenLine, MoreVertical, Trash2 } from '@/components/icons';
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { useTripDelete } from '@/lib/hooks';
import { TripDrawer } from './TripDrawer';

interface TripActionsSelectProps {
  tripId: string;
}

export function TripActionsSelect({ tripId }: TripActionsSelectProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const { mutate: deleteTrip } = useTripDelete();

  const handleDeleteTrip = () => {
    deleteTrip(
      { id: tripId },
      {
        onSuccess: () => {
          toast.success('Trip deleted successfully');
          setShowDeleteDialog(false);
          navigate({ to: '/operations/trips' });
        },
        onError: (error) => {
          console.error('Error deleting Trip:', error);
          toast.error(error instanceof Error && error.message ? error.message : 'Failed to delete Trip');
        },
      }
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="size-8 p-0 bg-content-primary-inverse hover:bg-content-primary-inverse/30 border-0 rounded-full flex items-center justify-center outline-none">
          <MoreVertical className="size-4 text-content-primary-bold" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="text-content-primary-bold focus:text-content-primary-bold"
            onClick={() => setIsEditDrawerOpen(true)}
          >
            <FilePenLine className="size-4 text-content-primary-bold" />
            Edit Trip
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="size-4" />
            Delete Trip
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove This Trip</AlertDialogTitle>
            <AlertDialogDescription>
              Flights in this trip will be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTrip}
              className="bg-content-negative-bold hover:bg-content-negative-bold/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Trip Drawer */}
      <TripDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => setIsEditDrawerOpen(false)}
        tripId={tripId}
      />
    </>
  );
}
